/**
 * SOLID Review
 * S — Strong. One concern: React's effect rule, run by a view model — which reactions to start, stop and restart, in which order.
 * O — Strong. A reaction is one entry of the list a view model declares; nothing here names one.
 * L — N/A — no hierarchy.
 * I — Strong. Three methods: a commit's two phases, and an unmount.
 * D — Strong. Imports nothing.
 */

/** One reaction: the facts it depends on, and what it starts — answering what stops it, or nothing. */
export interface IConfigReaction<TFacts> {
  readonly deps: (facts: TFacts) => readonly unknown[];
  readonly run: (facts: TFacts) => (() => void) | void;
}

/**
 * A VIEW MODEL'S EFFECTS (frontend-framework-oo P2, 2026-10-29, §28.294) — what a hook kept in `useEffect`s, declared as reactions
 * in the order the hook declared its effects, and run by React's own rule so the order in which they call the outside does not move:
 * after a commit, the reactions whose dependencies changed (compared one by one with `Object.is`, as React compares them) are
 * stopped in order, and then started in order; a reaction never started counts as changed. `stop()` is an unmount: every running
 * reaction stopped, in order, and the next commit starts them all again (React's StrictMode remounts a mount this way).
 *
 * **Two phases, as React runs them (P3, §28.295):** React stops the changed effects of EVERY component in a commit before it starts
 * any — a child's new effects start after its parent's old ones stopped. So the binding calls `stopChanged` in React's destroy phase
 * and `startChanged` in its create phase, both with the commit's facts, and a screen's model that holds the reactions of several
 * hooks (AppRoot's) stops and starts them where React would have.
 */
export class Reactions<TFacts> {
  private readonly last: Array<readonly unknown[] | null>;
  private readonly stops: Array<(() => void) | undefined>;

  constructor(private readonly list: ReadonlyArray<IConfigReaction<TFacts>>) {
    this.last = list.map(() => null);
    this.stops = list.map(() => undefined);
  }

  /**
   * A model's reactions, run over the facts of the model that composes it (P3, §28.295): AppRoot's model holds the reactions of the
   * controllers its hooks were, in the screen's order, each reading its own facts out of the screen's.
   */
  static over<TOwn, TFacts>(list: ReadonlyArray<IConfigReaction<TOwn>>, factsOf: (facts: TFacts) => TOwn): Array<IConfigReaction<TFacts>> {
    return list.map((reaction) => {
      const composed: IConfigReaction<TFacts> = { deps: (facts) => reaction.deps(factsOf(facts)), run: (facts) => reaction.run(factsOf(facts)) };
      return composed;
    });
  }

  /** React's destroy phase of a commit: the reactions whose dependencies these facts change are stopped, in order. */
  stopChanged(facts: TFacts): void {
    this.changesOf(facts).forEach((change, i) => {
      if (change !== null) this.stopOne(i);
    });
  }

  /**
   * React's create phase: the reactions whose dependencies these facts change are started, in order — each stopped first if it still
   * runs (a commit whose destroy phase did not reach this model).
   */
  startChanged(facts: TFacts): void {
    this.changesOf(facts).forEach((change, i) => {
      if (change === null) return;
      this.stopOne(i);
      this.last[i] = change;
      const stop = this.list[i]!.run(facts);
      this.stops[i] = stop === undefined ? undefined : stop;
    });
  }

  stop(): void {
    this.list.forEach((_reaction, i) => {
      this.stopOne(i);
      this.last[i] = null;
    });
  }

  /** Per reaction, its new dependencies when these facts change them — or null when they do not. */
  private changesOf(facts: TFacts): Array<readonly unknown[] | null> {
    return this.list.map((reaction, i) => {
      const now = reaction.deps(facts);
      const before = this.last[i];
      const changed = before === null || before.length !== now.length || now.some((dep, d) => !Object.is(dep, before[d]));
      return changed ? now : null;
    });
  }

  private stopOne(i: number): void {
    const stop = this.stops[i];
    this.stops[i] = undefined;
    stop?.();
  }
}
